import { useEffect, useRef } from "react";
import {
  downloadGoogleDriveFile,
  googleDriveDownloadToFile,
  isGoogleDriveReconnectRequiredError,
} from "@/lib/api/google-drive";
import { formatOnePickOAuthError } from "@/lib/google-picker";

export interface DriveOnePickImportPayload {
  fileIds: string[];
  files: File[];
}

interface UseDriveOnePickReturnOptions {
  enabled?: boolean;
  onImport: (payload: DriveOnePickImportPayload) => void | Promise<void>;
  onError?: (message: string) => void;
  onBusyChange?: (busy: boolean) => void;
}

function stripOnePickQueryParams(): { fileIds: string[]; error: string | null } {
  const params = new URLSearchParams(window.location.search);
  const driveFiles = params.get("driveFiles");
  const driveError = params.get("driveError");

  if (!driveFiles && !driveError) {
    return { fileIds: [], error: null };
  }

  params.delete("driveFiles");
  params.delete("driveError");
  const nextSearch = params.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}`;
  window.history.replaceState({}, "", nextUrl);

  return {
    fileIds: driveFiles ? driveFiles.split(",").map((id) => id.trim()).filter(Boolean) : [],
    error: driveError,
  };
}

export function useDriveOnePickReturn({
  enabled = true,
  onImport,
  onError,
  onBusyChange,
}: UseDriveOnePickReturnOptions): void {
  const onImportRef = useRef(onImport);
  const onErrorRef = useRef(onError);
  const onBusyChangeRef = useRef(onBusyChange);

  useEffect(() => {
    onImportRef.current = onImport;
  }, [onImport]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    onBusyChangeRef.current = onBusyChange;
  }, [onBusyChange]);

  useEffect(() => {
    if (!enabled) return;

    const { fileIds, error } = stripOnePickQueryParams();
    if (error) {
      const message = formatOnePickOAuthError(error);
      if (message) {
        onErrorRef.current?.(message);
      }
      return;
    }

    if (fileIds.length === 0) return;

    let cancelled = false;

    async function importFiles() {
      onBusyChangeRef.current?.(true);
      try {
        const files: File[] = [];
        for (const fileId of fileIds) {
          const downloaded = await downloadGoogleDriveFile({ fileId });
          files.push(googleDriveDownloadToFile(downloaded));
        }

        if (!cancelled) {
          await onImportRef.current({ fileIds, files });
        }
      } catch (err) {
        if (cancelled) return;
        if (isGoogleDriveReconnectRequiredError(err)) {
          onErrorRef.current?.("Google Drive authorization has expired. Reconnect to continue.");
          return;
        }
        onErrorRef.current?.(
          err instanceof Error ? err.message : "Failed to import from Google Drive"
        );
      } finally {
        if (!cancelled) {
          onBusyChangeRef.current?.(false);
        }
      }
    }

    void importFiles();

    return () => {
      cancelled = true;
      onBusyChangeRef.current?.(false);
    };
  }, [enabled]);
}
